import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { AuthStatus } from '../AuthStatus';
import { readPendingAssessment } from '../../lib/assessment/submission';
import { ASSESSMENT_LABELS, routes } from '../../lib/assessment/routes';
import { usePageMeta } from '../../lib/usePageMeta';

/**
 * Production's per-type titles. It serves this interstitial from three routes
 * with three titles; V2 serves one and reads the type from the pending
 * submission, falling back to the neutral title when there is none.
 */
const READY_TITLES: Record<string, string> = {
  bottleneck: 'Your Bottleneck Assessment Is Ready | High Performer OS',
  work_fit: 'Your Work Fit Assessment Is Ready | High Performer OS',
  perspective: 'Your Perspective Assessment Is Ready | High Performer OS',
};

/**
 * Post-submit interstitial for a signed-OUT user (Replit /results/ready parity).
 * The completed answers are already stored in sessionStorage; the user creates an
 * account here, and once signed in we route to /welcome, which claims (submits) the
 * pending assessment with the same submissionId.
 */
export function ResultsReady() {
  const { status } = useAuth();
  const [, navigate] = useLocation();
  const pending = readPendingAssessment(window.sessionStorage);

  usePageMeta({
    title:
      (pending ? READY_TITLES[pending.type] : null) ??
      'Your Assessment Is Ready | High Performer OS',
    noindex: true,
  });

  useEffect(() => {
    if (status === 'signed-in') {
      // The claim path wins whenever there is something to claim: `/welcome`
      // submits the stored answers before anything else can happen, and a
      // redirect past it would strand a completed assessment. With nothing
      // pending the visitor is simply signed in, so they go to their dashboard
      // rather than to the marketing home — which was the only destination that
      // existed when this was written.
      navigate(pending ? routes.welcome : routes.dashboard);
    }
  }, [status, pending, navigate]);

  if (!pending) {
    return (
      <section className="flex flex-col gap-3">
        <p className="text-foreground">There is no assessment ready to view.</p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-xl font-medium">Your {ASSESSMENT_LABELS[pending.type]} is ready</h2>
      <p className="text-foreground">
        Create an account or sign in to see your free result. Your answers are saved and will be
        scored as soon as you are signed in.
      </p>
      <AuthStatus />
    </section>
  );
}
